import { Paper, SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import { Theme } from "@mui/material/styles";
import React from "react";
import { IButtonGroupOption } from "../../ButtonGroup";
import SearchField from "../../SearchField";
import { IFilterModel, ISearchModel, TTableSortModel } from "../model/types";
import { ExtraExportButton } from "./TableFilters/ExtraExportButton";
import TableFilters from "./TableFilters/TableFilters";
import TablePageHeader, { ITablePageHeaderProps } from "./TablePageHeader";
import TableSort from "./TableSort";
import TableTabsGroup from "./TableTabsGroup";

export interface IExportStates {
  handleExportDraftReport?: () => void;
  isExporting?: boolean;
  isSavingAll?: boolean;
  hasChanges?: boolean;
  isBonusesCalculated?: boolean;
}
export interface ITablePageLayoutProps extends ITablePageHeaderProps {
  hideTablePageHeader?: boolean;
  children?: React.ReactNode;
  sx?: SxProps<Theme>;
  tableFilterModel?: IFilterModel;
  tableSortModel?: TTableSortModel;
  tableSearchModel?: ISearchModel;
  tabsOptions?: IButtonGroupOption<string | null>[];
  tabsValue?: string | null;
  allowedAgencies?: number[];
  handleTabsChange?: (v: string | null) => void;
  exportStates?: IExportStates;
}

function TablePageLayout({
  hideTablePageHeader,
  children,
  sx,
  tableSearchModel,
  tableFilterModel,
  tableSortModel,
  tabsOptions,
  tabsValue,
  allowedAgencies,
  handleTabsChange,
  exportStates,
  ...tablePageHeaderProps
}: ITablePageLayoutProps) {
  return hideTablePageHeader ? (
    <Box sx={{ flex: 1, minHeight: 0, ...sx }}>{children}</Box>
  ) : (
    <Box
      sx={{
        height: "100%",
        flex: 1,
        minWidth: 0,
        borderColor: "grey.300",
        display: "flex",
        flexDirection: "column",
        ...sx,
      }}
    >
      <TablePageHeader {...tablePageHeaderProps} />
      <Box
        sx={{
          pb: 2,
          minHeight: 0,
          flex: 1,
        }}
      >
        <Paper
          sx={{
            borderRadius: 4,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.14)",
            border: "1px solid",
            borderColor: "grey.200",
          }}
        >
          {tabsOptions && handleTabsChange && (
            <Box
              sx={{
                px: 2,
                borderBottom: "1px solid",
                borderColor: "grey.200",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Box
                className={
                  tablePageHeaderProps.disableTablePageHeader ? "disabled" : ""
                }
                sx={{
                  ["&.disabled"]: { opacity: "0.4", pointerEvents: "none" },
                }}
              >
                <TableTabsGroup<string | null>
                  value={tabsValue}
                  buttonsList={tabsOptions}
                  onClick={handleTabsChange}
                  allowedAgencies={allowedAgencies}
                />
              </Box>
            </Box>
          )}
          {tableSortModel ||
          tableFilterModel ||
          tableSearchModel ||
          exportStates ? (
            <Box
              className={
                tablePageHeaderProps.disableTablePageHeader ? "disabled" : ""
              }
              sx={{
                py: 5,
                px: 4,
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: 4,
                ["&.disabled"]: { opacity: "0.4", pointerEvents: "none" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {!!tableFilterModel && (
                  <TableFilters
                    tableFilterModel={tableFilterModel}
                    after={
                      tableSortModel ? (
                        <TableSort sortModel={tableSortModel} />
                      ) : null
                    }
                  />
                )}
              </Box>

              {!!exportStates && (
                <ExtraExportButton exportStates={exportStates} />
              )}
              {!!tableSearchModel && (
                <Box sx={{ maxWidth: 300, flexGrow: 1, ml: { md: "auto" } }}>
                  <SearchField
                    fullWidth
                    placeholder="Поиск"
                    value={tableSearchModel.value}
                    onChange={tableSearchModel.onSearch}
                    onCrossClick={() => tableSearchModel.onSearch("")}
                  />
                </Box>
              )}
            </Box>
          ) : null}
          {children}
        </Paper>
      </Box>
    </Box>
  );
}

export default TablePageLayout;
